import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DeviceAdditionalAttribute, DeviceAttribute, DeviceAttributeType } from '@devices';
import { SearchInputComponent } from '@ui';
import { debounceTime, skip } from 'rxjs';

type Attribute = (DeviceAdditionalAttribute | DeviceAttribute) & {
  value?: string | null;
};

type AttributeGroup = FormGroup<{
  checked: FormControl<boolean>;
  friendlyName: FormControl<string>;
  isStreamed: FormControl<boolean>;
  name: FormControl<string>;
  value: FormControl<string | null>;
}>;

/**
 * Component for displaying and managing device attributes and their
 * values - both PROPERTY and REAL_TIME depending on `attributeType` input.
 * It allows users to search, select, and modify attributes associated with a device.
 */
@Component({
  imports: [ReactiveFormsModule, SearchInputComponent, MatCheckboxModule, MatFormFieldModule, MatInputModule, MatSlideToggleModule],
  selector: 'avs-device-attributes-values',
  templateUrl: './device-attributes-values.component.html',
})
export class DeviceAttributesValuesComponent {
  attributeType = input(DeviceAttributeType.PROPERTY);
  changed = output<Attribute[]>();
  deviceAttributes = input.required<DeviceAdditionalAttribute[]>();
  modelAttributes = input.required<DeviceAttribute[]>();
  value = input<DeviceAdditionalAttribute[]>([]);

  protected allAttributes = computed(() => {
    const deviceAttributes = this.deviceAttributes()?.filter(attr => attr.attributeType === this.attributeType()) || [];
    const modelAttributes = this.modelAttributes()?.filter(attr => attr.attributeType === this.attributeType()) || [];

    const allPointsMap = new Map<string, Attribute>();

    modelAttributes.forEach(dp => allPointsMap.set(dp.name, dp));
    deviceAttributes.forEach(dp => allPointsMap.set(dp.name, dp));

    return Array.from(allPointsMap.values());
  });

  protected formGroups = {} as Record<string, AttributeGroup>;
  protected search = signal('');

  protected filteredAttributes = computed(() => {
    const search = this.search().toLowerCase();

    if (!search) {
      return this.allAttributes();
    }

    return this.allAttributes().filter(attr =>
      attr.friendlyName.toLowerCase().includes(search) ||
      attr.name.toLowerCase().includes(search),
    );
  });

  protected form: FormArray<AttributeGroup>;

  private fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.array<AttributeGroup>([]);

    effect(() => {
      const attributes = this.allAttributes();
      const values = this.value() ?? [];

      for (const attr of attributes) {
        if (this.formGroups[attr.name]) {
          continue;
        }

        const existing = values.find(v => v.name === attr.name);
        const checked = this.value()?.length ? !!existing : true;
        const isStreamed = !existing?.isManual;

        const control = this.fb.group({
          checked: [checked],
          friendlyName: [attr.friendlyName],
          isStreamed: [{ disabled: !checked, value: isStreamed }],
          name: [attr.name],
          value: [{
            disabled: !checked || isStreamed,
            value: existing?.value ?? attr.value ?? null,
          }],
        }) as AttributeGroup;

        this.formGroups[attr.name] = control;
        this.form.push(control);
      }
    });

    this.form.valueChanges
      .pipe(
        debounceTime(300),
        skip(1),
      )
      .subscribe(() => {
        const attributes = this.allAttributes();
        /**
         * valueChanges emits FormArray.value, which silently omits any disabled control's value,
         * so we need to get the raw value to include all controls
         */
        const toEmit = this.form.getRawValue()
          .filter(val => val.checked)
          .map(val => {
            const attr = attributes.find(a => a.name === val.name)!;

            return { ...attr, isManual: !val.isStreamed, value: val.value };
          });

        this.changed.emit(toEmit as Attribute[]);
      });
  }

  protected toggleGroupState(attrName: string, checked: boolean) {
    const group = this.formGroups[attrName];

    if (checked) {
      const isStreamed = group.get('isStreamed')?.value ?? true;

      group.get('isStreamed')?.enable({ emitEvent: false });
      if (!isStreamed) {
        group.get('value')?.enable({ emitEvent: false });
      }
    } else {
      group.get('isStreamed')?.disable({ emitEvent: false });
      group.get('value')?.patchValue(null, { emitEvent: false });
      group.get('value')?.disable({ emitEvent: false });
    }
  }

  protected toggleValueState(attrName: string, checked: boolean) {
    const group = this.formGroups[attrName];

    if (!checked) {
      group.get('value')?.enable({ emitEvent: false });
    } else {
      group.get('value')?.disable({ emitEvent: false });
    }
  }
}
